// Page-view tracker — fire-and-forget POST /api/track on every route change.
// Lightweight: uses sessionStorage for a stable session_id, no external libs.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "kiwami_session_id";

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      // simple opaque ID
      id =
        Date.now().toString(36) +
        "-" +
        Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export function usePageViewTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    // Skip admin-area routes to avoid biasing analytics
    if (location.pathname.startsWith("/admin")) return;
    if (lastPath.current === location.pathname + location.search) return;
    lastPath.current = location.pathname + location.search;

    const send = async () => {
      try {
        const payload = {
          event_type: "page_view",
          path: location.pathname,
          session_id: getOrCreateSessionId(),
          referrer:
            typeof document !== "undefined"
              ? document.referrer || null
              : null,
          language:
            typeof document !== "undefined"
              ? document.documentElement.lang || "fr"
              : "fr",
        };
        // beacon-style — fire and forget, don't await
        fetch(`${API}/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* ignore */
      }
    };
    queueMicrotask(send);
  }, [location.pathname, location.search]);
}

// Standalone tracker for other event types (e.g., order_submit)
export function track(event_type, extra = {}) {
  try {
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type,
        path:
          typeof window !== "undefined"
            ? window.location.pathname
            : "/",
        session_id: getOrCreateSessionId(),
        language:
          typeof document !== "undefined"
            ? document.documentElement.lang || "fr"
            : "fr",
        ...extra,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
